import {
  type RgbaColor,
  type RgbColor,
} from '@wandb/weave/common/util/render_babylon';
import React from 'react';

import {
  type MediaPanelCardControl,
  type TileMedia,
  type TileMediaSpec,
  type TileMediaTracking,
} from './MediaCard';
import {useGetTrackingCallbacks} from './PanelMediaBrowser/MediaBrowserPanelTileTrackingContext';
import {babylonPointCloudRenderer} from './PointCloudCard/babylonRenderer';
import {PointCloudViewer} from './PointCloudCard/PointCloudViewer';
import {usePointCloudScene} from './PointCloudCard/usePointCloudScene';

export interface Media3DProps {
  width: number;
  height: number;
  url: string;
  blob: Blob;
  controls?: MediaPanelCardControl;
  mediaKey: string;
  mediaPanelRefId: string;
  backgroundColor?: RgbaColor;
  pointColor?: RgbColor;
  useRightHandedSystem?: boolean;
  tileMedia: TileMedia | null;
  tileMediaSpec: TileMediaSpec | undefined;
  getMediaTracking?: (tileMediaSpec: TileMediaSpec) => TileMediaTracking | null;
}

/**
 * Workspace Object3D point-cloud card. Parse/preprocess live in
 * {@link usePointCloudScene}; Babylon draws through {@link PointCloudViewer}.
 * The Potree dense renderer is defined separately and gated in a follow-up.
 */
export const PointCloud = ({
  width,
  height,
  blob,
  mediaKey,
  mediaPanelRefId,
  controls,
  backgroundColor,
  pointColor,
  useRightHandedSystem,
  tileMedia,
  tileMediaSpec,
  getMediaTracking,
}: Media3DProps) => {
  const {scene, sourcePointCount, loadErrorMessage} = usePointCloudScene({
    blob,
    mediaKey,
    mediaPanelRefId,
    controls,
    pointColor,
  });

  const {trackSuccess, trackError} = useGetTrackingCallbacks(
    tileMedia,
    tileMediaSpec,
    getMediaTracking
  );

  if (loadErrorMessage) {
    console.log(`error ${loadErrorMessage}`);
    return <div className="card3d">Error: {loadErrorMessage}</div>;
  }

  if (scene == null) {
    return <div className="media-card group" />;
  }

  return (
    <PointCloudViewer
      scene={scene}
      width={width}
      height={height}
      renderer={babylonPointCloudRenderer}
      sourcePointCount={sourcePointCount}
      backgroundColor={backgroundColor}
      pointColor={pointColor}
      useRightHandedSystem={useRightHandedSystem}
      cameraControl={controls?.cameraControl}
      trackSuccess={trackSuccess}
      trackError={trackError}
    />
  );
};

export default PointCloud;
